import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Deal {
  id: string;
  name: string;
  stage: string;
  amount: number | null;
  total_amount: number | null;
  sales_consultant: string | null;
  sales_development: string | null;
  contact_id: string | null;
  created_at: string;
  contact?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  };
}

interface ConsultantData {
  consultant_id: string;
  consultant_name: string;
  count: number;
  deals: Deal[];
}

interface UseDealsWithoutScheduledCallsOptions {
  consultantFilter?: string;
  sdrFilter?: string;
  sourceFilter?: string;
  channelFilter?: string;
}

export function useDealsWithoutScheduledCalls(
  deals: Deal[],
  users: any[],
  options: UseDealsWithoutScheduledCallsOptions = {}
) {
  const [scheduledCallDealIds, setScheduledCallDealIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Fetch all deals that have future scheduled calls
  useEffect(() => {
    const fetchScheduledCalls = async () => {
      setLoading(true);
      try {
        const now = new Date().toISOString();
        
        const { data, error } = await supabase
          .from('calls')
          .select('deal_id')
          .not('deal_id', 'is', null)
          .gte('scheduled_for', now)
          .in('status', ['scheduled', 'in-progress']);

        if (error) throw error;

        const dealIdsWithCalls = new Set(
          (data || [])
            .map(c => c.deal_id)
            .filter(Boolean) as string[]
        );
        
        setScheduledCallDealIds(dealIdsWithCalls);
      } catch (err) {
        console.error('Error fetching scheduled calls:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchScheduledCalls();
  }, []);

  // Filter active deals and apply user filters
  const activeDeals = useMemo(() => {
    return deals.filter(d => {
      if (d.stage === 'won' || d.stage === 'lost') return false;
      
      // Apply consultant filter
      if (options.consultantFilter && options.consultantFilter !== 'all') {
        if (options.consultantFilter === 'unassigned') {
          if (d.sales_consultant) return false;
        } else {
          if (d.sales_consultant !== options.consultantFilter) return false;
        }
      }
      
      // Apply SDR filter
      if (options.sdrFilter && options.sdrFilter !== 'all') {
        if (options.sdrFilter === 'unassigned') {
          if (d.sales_development) return false;
        } else {
          if (d.sales_development !== options.sdrFilter) return false;
        }
      }
      
      return true;
    });
  }, [deals, options.consultantFilter, options.sdrFilter]);

  // Find deals without scheduled calls
  const dealsWithoutCalls = useMemo(() => {
    return activeDeals.filter(d => !scheduledCallDealIds.has(d.id));
  }, [activeDeals, scheduledCallDealIds]);

  // Group by sales consultant
  const dataByConsultant = useMemo(() => {
    const grouped: Record<string, ConsultantData> = {};
    
    dealsWithoutCalls.forEach(deal => {
      const consultantId = deal.sales_consultant || 'unassigned';
      
      if (!grouped[consultantId]) {
        const consultant = users.find(u => u.id === consultantId);
        grouped[consultantId] = {
          consultant_id: consultantId,
          consultant_name: consultant?.display_name || 'Unassigned',
          count: 0,
          deals: []
        };
      }
      
      grouped[consultantId].count++;
      grouped[consultantId].deals.push(deal);
    });

    // Sort by count descending
    return Object.values(grouped).sort((a, b) => b.count - a.count);
  }, [dealsWithoutCalls, users]);

  return {
    dataByConsultant,
    dealsWithoutCalls,
    totalWithoutCalls: dealsWithoutCalls.length,
    loading
  };
}
